import { captureException } from '@sentry/react';
import debounce from 'debounce';
import { useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import { create } from 'zustand';
import { SOCKET_OPTIONS, WsExecutes, WsNamespace } from '../constants/ws';
import {
  SchemaDryRunOrderCreatedDto,
  SchemaDryRunOrderFailedDto,
  SchemaSubmitDryOrderDto,
} from '../generated/apiTypes';
import { validateAmount } from '../lib/amountError';
import { apiWsUrl } from '../lib/config';
import { Gwei } from '../lib/gwei';
import { getNonceInNanoseconds, nowInSeconds } from '../lib/time';
import { ProductType } from './useProducts';

const transformDryRunResult = (
  result: SchemaDryRunOrderCreatedDto | SchemaDryRunOrderFailedDto
) => ({
  code: result.code,
  marginRequired: Gwei.toGwei(result.marginRequired),
  marginAvailable: Gwei.toGwei(result.marginAvailable),
  totalUsedMargin: Gwei.toGwei(result.totalUsedMargin),
  riskUsed: Gwei.toGwei(result.riskUsed),
  riskAvailable: Gwei.toGwei(result.riskAvailable),
});
export type DryRunResult = ReturnType<typeof transformDryRunResult>;

export interface DryRunStore {
  isConnected: boolean;
  isConnecting: boolean;
  result: DryRunResult | null;
  lastUpdated: number | null;
  socket: Socket | null;
  error: string | null;
  pendingPayload: SchemaSubmitDryOrderDto | null;
  requestId: number;
  mostRecentPayload: SchemaSubmitDryOrderDto | null;

  connect: () => void;
  reconnect: () => void;
  dryRun: (payload: SchemaSubmitDryOrderDto, product: ProductType) => void;
  disconnect: () => void;
  reset: () => void;
}

export const unexpectedErrorMessage = `We're having unexpected problems, team has been notified.`;

const initialState: Omit<DryRunStore, 'connect' | 'reconnect' | 'dryRun' | 'disconnect' | 'reset'> =
  {
    isConnected: false,
    isConnecting: false,
    result: null,
    lastUpdated: null,
    socket: null,
    error: null,
    pendingPayload: null,
    requestId: 0,
    mostRecentPayload: null,
  };
const EMIT_DEBOUNCE = 200;

export const useDryRunStore = create<DryRunStore>()((set, get) => {
  const debouncedEmitDryRun = debounce((socket: Socket, payload: SchemaSubmitDryOrderDto) => {
    const currentRequestId = get().requestId;

    socket.emit(
      WsExecutes.ORDER_DRY_RUN,
      payload,
      (response: SchemaDryRunOrderCreatedDto | SchemaDryRunOrderFailedDto) => {
        const latestRequestId = get().requestId;

        // Only update state if this response matches the latest request
        if (currentRequestId === latestRequestId) {
          set({
            mostRecentPayload: payload,
            result: transformDryRunResult(response),
            lastUpdated: Date.now(),
            error: null,
          });
        }
      }
    );
  }, EMIT_DEBOUNCE);

  return {
    ...initialState,
    connect: () => {
      const { isConnecting, socket: existingSocket } = get();

      // Guard: Only connect once
      if (existingSocket?.connected || isConnecting) return;
      if (existingSocket) {
        existingSocket.removeAllListeners();
        set({ socket: null });
      }

      set({ isConnecting: true });

      const url = new URL(WsNamespace.EXECUTE, apiWsUrl).toString();
      const socket = io(url, SOCKET_OPTIONS);

      socket.on('connect', () => {
        set({ isConnected: true, isConnecting: false });
        // Send pending payload if exists
        const { pendingPayload, mostRecentPayload } = get();
        if (pendingPayload) {
          debouncedEmitDryRun(socket, pendingPayload);
          set({ pendingPayload: null });
          return;
        }
        // We just reconnected but had a previous result, emit it again to populate fresh result.
        if (mostRecentPayload) {
          // Update timestamps to current values to avoid stale timestamp issues
          const updatedPayload: SchemaSubmitDryOrderDto = {
            ...mostRecentPayload,
            data: {
              ...mostRecentPayload.data,
              nonce: getNonceInNanoseconds().toString(),
              signedAt: nowInSeconds(),
            },
          };
          debouncedEmitDryRun(socket, updatedPayload);
          return;
        }
      });

      socket.on('exception', (arrayOfErrors) => {
        const error = Array.isArray(arrayOfErrors) ? arrayOfErrors[0] : arrayOfErrors;
        console.error('WebSocket exception:', error);
        captureException(error, { data: 'socket exception' });
        set({
          result: null,
          lastUpdated: null,
          error: unexpectedErrorMessage,
        });
      });

      socket.on('connect_error', (error) => {
        captureException(error, { data: 'socket connect error' });
        console.error('Socket connection error:', error);
        set({
          isConnected: false,
          isConnecting: false,
          error: unexpectedErrorMessage,
        });
      });

      socket.on('error', (error) => {
        captureException(error, { data: 'socket error' });

        console.error('Socket error:', error);
        set({
          result: null,
          lastUpdated: null,
          error: unexpectedErrorMessage,
        });
      });
      socket.connect();
      set({ socket });
    },

    reconnect: () => {
      const { socket, connect, mostRecentPayload } = get();
      socket?.removeAllListeners();
      socket?.disconnect();
      // Keep the most recent payload to re-emit it after reconnecting
      set({ ...initialState, mostRecentPayload });
      connect();
    },

    dryRun: (payload, product) => {
      const { isConnected, isConnecting, socket, requestId } = get();
      const amountSizeErrors = validateAmount(
        Gwei.toGwei(payload.data.quantity),
        product,
        payload.data.close
      );
      if (amountSizeErrors) {
        const error = Error(
          `Amount size errors, this should be checked before calling dry run, error: ${amountSizeErrors}`
        );
        captureException(error);
        throw error;
      }

      // Increment request ID to track the latest request
      const newRequestId = requestId + 1;

      set({ requestId: newRequestId });

      if (isConnecting) {
        set({ pendingPayload: payload });
        return;
      }
      if (!isConnected || !socket) {
        set({ pendingPayload: payload });
        get().connect();
        return;
      }

      debouncedEmitDryRun(socket, payload);
    },

    disconnect: () => {
      const { socket } = get();
      if (socket && socket.connected) {
        socket.disconnect();
      }
      set(initialState);
    },

    reset: () => {
      // Increment request ID to invalidate any pending responses
      const newRequestId = get().requestId + 1;

      // Cancel the debounced function if it's pending
      debouncedEmitDryRun.clear();

      set({
        result: null,
        lastUpdated: null,
        pendingPayload: null,
        mostRecentPayload: null,
        requestId: newRequestId,
      });
    },
  };
});

export const useDryRun = () => {
  const store = useDryRunStore();
  const { connect } = store;
  useEffect(() => {
    // We always want a connected dry run socket, so no disconnect.
    connect();
  }, [connect]);

  return store;
};
